import { memo, useEffect, useMemo, useRef } from 'react';
import type { RefObject } from 'react';
import { Trans } from 'react-i18next';

type StaticTranslationProps = {
  readonly initialSeconds: number;
  readonly secondsElementRef: RefObject<HTMLSpanElement>;
};

/**
 * Keep the translated phrase out of the timer render path. Browser translators
 * may replace its text nodes, so the changing number is updated imperatively
 * inside its own stable element instead of through React reconciliation.
 */
const StaticTranslation = memo(
  ({ initialSeconds, secondsElementRef }: StaticTranslationProps) => {
    const values = useMemo(() => ({ seconds: initialSeconds }), [initialSeconds]);
    const components = useMemo(
      () => ({
        span: <span key="counter" ref={secondsElementRef} data-testid="resend-seconds" />,
      }),
      [secondsElementRef]
    );

    return (
      <Trans
        i18nKey="description.resend_after_seconds"
        values={values}
        components={components}
      />
    );
  }
);

type Props = {
  readonly seconds: number;
};

const TranslationSafeCountdown = ({ seconds }: Props) => {
  const secondsElementRef = useRef<HTMLSpanElement>(null);
  const initialSeconds = useRef(seconds).current;

  useEffect(() => {
    if (secondsElementRef.current) {
      // This node is intentionally updated outside React so browser translators
      // can replace its descendants without breaking React reconciliation.
      // eslint-disable-next-line @silverhand/fp/no-mutation
      secondsElementRef.current.textContent = String(seconds);
    }
  }, [seconds]);

  return (
    <StaticTranslation
      initialSeconds={initialSeconds}
      secondsElementRef={secondsElementRef}
    />
  );
};

export default TranslationSafeCountdown;
